/**
 * ProfilePage — User profile with working Subscription & Billing button.
 *
 * Manage Subscription / Billing calls POST /v1/subscription/portal to get
 * a Chargebee portal session URL, then navigates to it.
 */
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useHousehold } from '../../hooks/useHousehold';
import { useApi } from '../../hooks/useApi';
import type { SubscriptionStatusResponse } from '@tabletryb/shared';
import { User, Mail, CreditCard, Loader2, Home, Pencil, Check, X, Settings, ExternalLink, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const ProfilePage: React.FC = () => {
  const { user, getToken } = useAuth();
  const { household, loading: householdLoading, refetch: refetchHousehold } = useHousehold();
  const api = useApi();
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');

  // Household name editing
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');

  // Planning window setting                                       
  const [planningWindow, setPlanningWindow] = useState<number>(1);
  const [savingWindow, setSavingWindow] = useState(false);             

  const isPrimary = user?.role === 'primary';

  // Sync planning window when household data loads                
  React.useEffect(() => {
    const weeks = (household as any)?.settings?.planningWindowWeeks;
    if (weeks) setPlanningWindow(weeks);
  }, [household]);                                                     

  // TODO: Fetch isAccountHolder from household membership record
  const isAccountHolder = user?.role === 'primary'; // Simplified for now

  // Subscription status — drives manage-subscription routing by source (Chargebee vs IAP).
  const [subStatusChecked, setSubStatusChecked] = useState(false);
  const [subStatus, setSubStatus] = useState<SubscriptionStatusResponse | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const loadSubStatus = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/v1/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const data: SubscriptionStatusResponse = await res.json();
          setSubStatus(data);
        }
      } catch (err) {
        console.error('Failed to load subscription status:', err);
      } finally {
        if (!cancelled) setSubStatusChecked(true);
      }
    };
    loadSubStatus();
    return () => { cancelled = true; };
  }, [getToken]);

  const startEditingName = () => {
    setEditName(household?.name || '');
    setNameError('');
    setEditingName(true);
  };

  const handleSaveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed) { setNameError('Tribe name is required.'); return; }
    if (trimmed === household?.name) { setEditingName(false); return; }

    setSavingName(true);
    setNameError('');
    try {
      await api.put(`/v1/households/${user?.householdId}`, { name: trimmed });
      await refetchHousehold();
      setEditingName(false);
    } catch (err: any) {
      setNameError(err.message || 'Failed to update household name.');
    } finally {
      setSavingName(false);
    }
  };

  const handlePlanningWindowChange = async (weeks: number) => {
    if (!user?.householdId) return;
    setPlanningWindow(weeks);
    setSavingWindow(true);
    try {
      await api.put(`/v1/households/${user!.householdId}/settings`, {
        planningWindowWeeks: weeks,
      });
    } catch (err: any) {
      console.error('Failed to update planning window:', err);
    } finally {
      setSavingWindow(false);
    }
  };                                                              

  const openBillingPortal = async () => {
    setBillingLoading(true);
    setBillingError('');

    try {
      const token = await getToken();

      const response = await fetch(`${API_URL}/v1/subscription/portal`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to open Subscription & Billing portal (${response.status})`);
      }

      const data = await response.json();

      if (data.portalUrl) {
        window.location.href = data.portalUrl;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err: any) {
      console.error('Subscription & Billing portal error:', err);
      setBillingError(err.message || 'Failed to open Subscription & Billing portal.');
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Profile</h1>

      <div className="profile-card">
        <div className="profile-avatar">
          {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
        </div>

        <div className="profile-fields">
          <div className="profile-field">
            <User size={16} />
            <div>
              <label>First Name</label>
              <p>{user?.firstName || 'Not set'}</p>
            </div>
          </div>

          <div className="profile-field">
            <User size={16} />
            <div>
              <label>Last Name</label>
              <p>{user?.lastName || 'Not set'}</p>
            </div>
          </div>

          <div className="profile-field">
            <User size={16} />
            <div>
              <label>Preferred Name</label>
              <p>{user?.displayName || 'Not set'}</p>
            </div>
          </div>

          <div className="profile-field">
            <Mail size={16} />
            <div>
              <label>Email</label>
              <p>{user?.email || 'Not set'}</p>
            </div>
          </div>

          <div className="profile-field">
            <Home size={16} />
            <div style={{ flex: 1 }}>
              <label>Tribe</label>
              {editingName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <input
                    className="form-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                    disabled={savingName}
                    autoFocus
                    style={{ fontSize: '15px', padding: '4px 8px', flex: 1, maxWidth: '280px' }}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={savingName}
                    title="Save"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', padding: '4px', display: 'flex' }}
                  >
                    {savingName ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    disabled={savingName}
                    title="Cancel"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '4px', display: 'flex' }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <p>{householdLoading ? '...' : household?.name || 'None'}</p>
                  {isPrimary && !householdLoading && (
                    <button
                      onClick={startEditingName}
                      title="Edit tribe name"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '2px', display: 'flex' }}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
              )}
              {nameError && (
                <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px', display: 'block' }}>{nameError}</span>
              )}
              {user?.householdId && (
                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>
                  ID: {user.householdId}
                </span>
              )}
            </div>
          </div>

          <div className="profile-field">
            <span className="profile-role-badge">
              {user?.role === 'primary' ? 'Primary User' : 'Member'}
            </span>
          </div>
        </div>
        
        {isPrimary && (
          <>
            <div className="profile-divider" />
            <div className="profile-billing">
              <h3><Settings size={18} /> Tribe Settings</h3>

              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>
                  Planning window
                </label>
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '8px' }}>
                  How many weeks to display on the Calendar Planner tab. A longer window lets you
                  schedule meals further ahead.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select
                    className="form-input"
                    value={planningWindow}
                    onChange={(e) => handlePlanningWindowChange(Number(e.target.value))}
                    disabled={savingWindow}
                    style={{ width: '160px', fontSize: '0.9rem', padding: '6px 8px' }}
                  >
                    <option value={1}>1 week</option>
                    <option value={2}>2 weeks</option>
                    <option value={3}>3 weeks</option>
                    <option value={4}>4 weeks</option>
                  </select>
                  {savingWindow && <Loader2 size={14} className="spin" />}
                </div>
              </div>
            </div>
          </>
        )}

        {isAccountHolder && (
          <>
            <div className="profile-divider" />
            <div className="profile-billing">
              <h3><CreditCard size={18} /> Subscription & Billing</h3>

              {subStatus?.hasConflictingSources && (
                <div style={{
                  padding: '0.75rem 1rem',
                  marginBottom: '1rem',
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  borderRadius: '8px',
                  color: '#92400E',
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                }}>
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem' }}>
                    <AlertTriangle size={16} /> You may be billed twice
                  </strong>
                  <p style={{ margin: '0 0 0.5rem' }}>
                    We found an active subscription from both the app store and the web, so you may be
                    charged for both. To avoid double-billing, cancel one of them: cancel the app store
                    subscription from your device settings (link below), and cancel the web subscription
                    through the billing portal.
                  </p>
                  <button
                    className="btn btn-secondary"
                    onClick={openBillingPortal}
                    disabled={billingLoading}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    {billingLoading ? (
                      <>
                        <Loader2 size={14} className="spin" />
                        Opening...
                      </>
                    ) : (
                      'Cancel web subscription (billing portal)'
                    )}
                  </button>
                  {billingError && (
                    <p style={{ margin: '0.5rem 0 0', color: '#DC2626', fontSize: '0.8rem' }}>
                      {billingError}
                    </p>
                  )}
                </div>
              )}

              {!subStatusChecked ? (
                <Loader2 size={16} className="spin" style={{ color: 'var(--color-text-muted)' }} />
              ) : subStatus?.subscriptionSource === 'apple_iap' ? (
                <>
                  <p>Your subscription is billed through the App Store. Manage or cancel it on your Apple device.</p>
                  <a
                    className="btn btn-secondary"
                    href="https://apps.apple.com/account/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
                  >
                    Manage on the App Store <ExternalLink size={14} />
                  </a>
                </>
              ) : subStatus?.subscriptionSource === 'google_iap' ? (
                <>
                  <p>Your subscription is billed through Google Play. Manage or cancel it on your Android device.</p>
                  <a
                    className="btn btn-secondary"
                    href="https://play.google.com/store/account/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
                  >
                    Manage on Google Play <ExternalLink size={14} />
                  </a>
                </>
              ) : subStatus?.subscriptionSource === 'chargebee' ? (
                <>
                  <p>Manage your subscription, payment method, view invoices, or cancel your plan.</p>

                  {billingError && (
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      marginBottom: '0.75rem',
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      borderRadius: '6px',
                      color: '#DC2626',
                      fontSize: '0.8rem',
                    }}>
                      {billingError}
                    </div>
                  )}

                  <button
                    className="btn btn-secondary"
                    onClick={openBillingPortal}
                    disabled={billingLoading}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    {billingLoading ? (
                      <>
                        <Loader2 size={14} className="spin" />
                        Opening...
                      </>
                    ) : (
                      'Manage Subscription / Billing'
                    )}
                  </button>
                </>
              ) : (
                <>
                  <p>You don't have an active subscription yet. Subscribe to keep planning meals after your trial.</p>
                  <a
                    className="btn btn-secondary"
                    href="/app/subscribe"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
                  >
                    View Plans
                  </a>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;